import { apiClient } from './client';
import { LookupRecord } from './masterData';
import { CreateQualificationPayload } from './academic';
import { getOwnSignature, getOwnSignatureImage, saveOwnSignature } from './studentSignature';
import { listPortalDocuments, uploadPortalDocument } from './portal';
import { Qualification } from '../types/academic';
import { StudentListItem } from '../types/student';
import type { ApplicationFormApi } from '../pages/students/wizard/applicationFormApi';

/** University Application Form via the Student Portal (2026-11-06). */

export type ApplicationFormStatus = 'NOT_SENT' | 'INVITATION_SENT' | 'IN_PROGRESS' | 'SUBMITTED' | 'REJECTED' | 'RESUBMITTED' | 'APPROVED';
export type ApplicationCompletionMethod = 'STUDENT_PORTAL' | 'COUNSELOR';

export interface ApplicationForm {
  studentId: number | null;
  applicationNumber?: string;
  status: ApplicationFormStatus;
  method: ApplicationCompletionMethod | null;
  invitedAt?: string | null;
  submittedAt?: string | null;
  reviewedAt?: string | null;
  reviewedBy?: string | null;
  rejectionReason?: string | null;
  registered?: boolean;
  portalAccount: { loginEmail: string; active: boolean; passwordSet: boolean; lastLoginAt: string | null } | null;
  submissions: Array<{ version: number; submittedAt: string; decision: 'APPROVED' | 'REJECTED' | null; decidedAt: string | null; decidedBy: string | null; reason: string | null }>;
}

export interface ApplicationReviewRow {
  studentId: number;
  leadId: number | null;
  leadNumber: string | null;
  applicationNumber: string;
  name: string;
  status: ApplicationFormStatus;
  submittedAt: string | null;
  version: number;
  branch: string | null;
  counsellor: string | null;
}

export interface InvitationResult {
  invite: { status: 'SENT' | 'FAILED'; channel: string; errorMessage: string | null; sentAt: string };
  form: ApplicationForm;
}

// --------------------------------------------------------------------- staff

export const getLeadApplicationForm = async (leadId: number): Promise<ApplicationForm> => {
  const { data } = await apiClient.get(`/leads/${leadId}/application-form`);
  return data.data;
};
export const sendApplicationInvitation = async (leadId: number): Promise<InvitationResult> => {
  const { data } = await apiClient.post(`/leads/${leadId}/application-form/invite`);
  return data.data;
};
export const chooseCounselorMethod = async (studentId: number): Promise<ApplicationForm> => {
  const { data } = await apiClient.post(`/students/${studentId}/application-form/counselor`);
  return data.data;
};
export const listApplicationReviews = async (): Promise<ApplicationReviewRow[]> => {
  const { data } = await apiClient.get('/students/application-reviews');
  return data.data;
};
export const approveApplication = async (studentId: number): Promise<ApplicationForm> => {
  const { data } = await apiClient.post(`/students/${studentId}/application-form/approve`);
  return data.data;
};
export const rejectApplication = async (studentId: number, reason: string): Promise<ApplicationForm> => {
  const { data } = await apiClient.post(`/students/${studentId}/application-form/reject`, { reason });
  return data.data;
};

// -------------------------------------------------------------------- portal

export interface OwnApplication {
  form: ApplicationForm;
  editable: boolean;
  student: StudentListItem;
}

export const getOwnApplication = async (): Promise<OwnApplication> => {
  const { data } = await apiClient.get('/portal/application');
  return data.data;
};
export const saveOwnApplication = async (payload: Record<string, unknown>): Promise<OwnApplication> => {
  const { data } = await apiClient.patch('/portal/application', payload);
  return data.data;
};
export const submitOwnApplication = async (): Promise<OwnApplication> => {
  const { data } = await apiClient.post('/portal/application/submit');
  return data.data;
};

/** The application form's steps, pointed at the student's own endpoints. */
const PORTAL_LOOKUP = /^\/(?:master-data\/)?([a-z-]+)$/;
export const portalApplicationFormApi: ApplicationFormApi = {
  mode: 'portal',
  listLookup: async (path, params) => {
    const resource = PORTAL_LOOKUP.exec(path)?.[1];
    if (!resource) throw new Error(`Not available in the portal: ${path}`);
    const { data } = await apiClient.get(`/portal/lookups/${resource}`, { params });
    return data.data as LookupRecord[];
  },
  listQualifications: async () => {
    const { data } = await apiClient.get('/portal/application/qualifications');
    return data.data as Qualification[];
  },
  createQualification: async (_studentId, payload: CreateQualificationPayload) => {
    const { data } = await apiClient.post('/portal/application/qualifications', payload);
    return data.data as Qualification;
  },
  deleteQualification: async (qualificationId) => {
    await apiClient.delete(`/portal/application/qualifications/${qualificationId}`);
  },
  listDocuments: () => listPortalDocuments(),
  uploadDocument: (_studentId, file, documentTypeId) => uploadPortalDocument(file, documentTypeId),
  getSignature: () => getOwnSignature(),
  getSignatureImage: () => getOwnSignatureImage(),
  saveSignature: (_studentId, method, file) => saveOwnSignature(method, file),
  getStudent: async () => (await getOwnApplication()).student,
};
